import React, { useState, useEffect } from "react";
import { unstable_batchedUpdates } from "react-dom";
import jump from "jump.js";
import debounce from "lodash/debounce";
import qs from "query-string";
import { withRouter } from "react-router";
import { getFilters, getObjects } from "~src/api/get";
import buildUrl from "~utils/buildUrl";
import FilterContextProvider, { FilterContext } from "~contextProviders/Filter";
import { favoritesContextData } from "~contextProviders/FavoritesContextProvider";
import currencyMap from "~constants/currencyMap";
import Filters from "~components/Filters";
import Listing from "~components/Listing";
import { currencyContextData } from "~contextProviders/CurrencyContextProvider";
import { COMPONENT_IDS } from '~src/constants';

import styles from "./styles.module.css";
import { ComponentIdContextProvider } from '~contextProviders/uiIds/ComponentIdContextProvider';

const Search = ({
  listingConfig,
  initialFilters = [],
  initialObjects = [],
  location: { pathname, search },
  history = {},
}) => {
  const componentId = COMPONENT_IDS.Search;

  const {
    catalog: { type },
    common: { timeoutFiltersUpdate },
    catalog,
  } = listingConfig;

  const {
    favoriteIds,
    hasFavorites,
    toggleFavorite,
  } = favoritesContextData.useCtx();
  const { currency } = currencyContextData.useCtx();

  const [filters, setFilters] = useState([...initialFilters]);
  const [objects, setObjects] = useState([...initialObjects]);
  const [showListingPreloader, setShowListingPreloader] = useState(true);

  const setAreObjectsLoading = useState([])[1];

  const jumpToListingNode = () => {
    const node = document.getElementById("listing");
    if (node) {
      jump(node, {
        duration: 1000,
        offset: -100,
      });
    }
  };

  const updateHistory = (query) => {
    history.push(buildUrl(pathname, query));
  };

  const updateFilters = debounce((query) => {
    updateHistory(query);
  }, timeoutFiltersUpdate);

  const fetchFilters = (query) => {
    getFilters({ query }).then(({ data }) => {
      if (initialFilters.length === 0) {
        setFilters(data);
      }
    });
  };

  const fetchObjects = (query = {}) => {
    setAreObjectsLoading(true);

    getObjects({ query })
      .then(({ data: { items } }) => {
        unstable_batchedUpdates(() => {
          setObjects(items);
          setAreObjectsLoading(false);
          setShowListingPreloader(false);
        });
      })
      .catch(() => setAreObjectsLoading(false));
  };

  const createQuery = (queryParams = {}) => {
    let query = {
      currencyId: currencyMap[currency].id,
      ...qs.parse(search),
      ...queryParams,
    };

    if (
      catalog.type === 'listingByQuery' ||
      catalog.type === 'catalogByQuery'
    ) {
      query = { ...catalog.query, ...query };
    }

    if (catalog.type === 'listingByIds') {
      query.ids = catalog.list.join(',');
    }

    if (catalog.type === 'favorite') {
      query.ids = favoriteIds.join(',');
    }

    return query
  }

  const dependencies = [search, currency];

  if (type === 'favorite') {
    dependencies.push(favoriteIds.length);
  }
  
  useEffect(() => {
    const query = createQuery()
  
    fetchObjects(query);
  }, dependencies);
  
  useEffect(() => {
    const query = createQuery()

    fetchFilters(query)
  }, []);

  const showFilters =
    type !== "favorite" && type !== "listingByQuery" && type !== "listingByIds";

  return (
    <ComponentIdContextProvider componentId={componentId}>
      <div className={styles['search']}>
        <FilterContextProvider
          filters={filters}
          updateFilters={updateFilters}
          onSearch={jumpToListingNode}
        >
          <FilterContext.Consumer>
            {({ getCategory, getActiveFilters }) => {
              return (
                <div>
                  {showFilters && (
                    <Filters objects={objects} currency={currency} />
                  )}
                  <Listing
                    config={listingConfig}
                    items={objects}
                    category={getCategory()}
                    typeListing={getActiveFilters().typeListing}
                    showPreloader={showListingPreloader}
                    favorites={{ hasFavorites, toggleFavorite }}
                  />
                </div>
              );
            }}
          </FilterContext.Consumer>
        </FilterContextProvider>
      </div>
    </ComponentIdContextProvider>
  );
};

export default withRouter(Search);
